import { useEffect, useRef, useState } from 'react'

import { getMeta, postFeasibility, postParse } from './api.js'
import { loadFormState, saveFormState } from './lib/formStorage.js'
import { decodeTrip, encodeTrip } from './lib/shareUrl.js'
import Results from './components/Results.jsx'
import SearchBox from './components/SearchBox.jsx'
import SiteNav from './components/SiteNav.jsx'
import TripForm, { emptyMember } from './components/TripForm.jsx'
import { track } from './track.js'

// Fields the search box may fill. Tracked individually so a background
// refinement can never overwrite something the user has since typed.
const PARSEABLE_FIELDS = ['members', 'budget_per_person_usd', 'days', 'rooms']

const sameValue = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null)

// Everything about a parse that could change what the form shows. Used only to
// decide whether the smart pass actually said something new — if it matches the
// fast pass, the user is told nothing.
const parseSignature = (res) =>
  JSON.stringify([
    res?.payload?.members ?? [],
    res?.payload?.budget_per_person_usd ?? null,
    res?.payload?.days ?? null,
    res?.payload?.rooms ?? null,
    res?.focus ?? [],
    res?.needs_disambiguation ?? [],
    res?.home_iata_candidates ?? [],
    res?.unresolved ?? []
  ])

// "Usable" means the engine could eventually answer, which needs a passport.
// An AMBIGUOUS nationality counts: "I'm Korean" resolves to one with a single
// tap, and treating it as unusable would make the disambiguation step
// unreachable for the very case it exists for. A parse that found only a budget
// still counts as nothing usable — the form cannot be submitted from it.
const isUsable = (res) =>
  (res?.payload?.members ?? []).some((m) => (m?.passports ?? []).length > 0) ||
  (res?.needs_disambiguation ?? []).length > 0

const DEFAULT_TRIP = {
  members: [emptyMember()],
  budget_per_person_usd: '',
  days: ''
}

// Held credentials as [{ country, type: 'visa' | 'residence', permanent }]. New
// links carry held_credentials directly; OLD links carry held_permits (list of
// ISO2 strings), which decode as residence permits so the toggle shows
// "Residence permit". `permanent` only ever means anything for a residence
// permit and defaults to false (temporary) — the safe default; some
// destinations require a permanent permit and refuse/deport a temporary one,
// so a restored share link or saved form must never silently upgrade to
// permanent.
function normalizeCredentials(m) {
  if (Array.isArray(m?.held_credentials)) {
    return m.held_credentials
      .filter((c) => c && typeof c.country === 'string')
      .map((c) => ({
        country: c.country,
        type: c.type === 'residence' ? 'residence' : 'visa',
        permanent: c.type === 'residence' && c.permanent === true
      }))
  }
  if (Array.isArray(m?.held_permits)) {
    return m.held_permits
      .filter((p) => typeof p === 'string')
      .map((p) => ({ country: p, type: 'residence', permanent: false }))
  }
  return []
}

function normalizeMembers(members) {
  const arr = (Array.isArray(members) ? members : []).map((m) => ({
    name: m?.name || '',
    passports: Array.isArray(m?.passports) ? m.passports : [],
    home_iata: m?.home_iata || '',
    held_credentials: normalizeCredentials(m)
  }))
  return arr.length ? arr : [emptyMember()]
}

function tripFromUrl() {
  const encoded = new URLSearchParams(window.location.search).get('t')
  if (!encoded) return null
  const trip = decodeTrip(encoded)
  if (typeof trip !== 'object' || trip === null || !Array.isArray(trip.members)) {
    throw new Error('shared trip is not a trip object')
  }
  return { ...trip, members: normalizeMembers(trip.members) }
}

// A saved draft of the in-progress form, or null if none/blocked/invalid.
function tripFromDraft() {
  const draft = loadFormState()
  if (!draft) return null
  return { ...draft, members: normalizeMembers(draft.members) }
}

export default function App() {
  const [meta, setMeta] = useState(null)
  const [trip, setTrip] = useState(DEFAULT_TRIP)
  const [view, setView] = useState('form')
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)
  const [shareState, setShareState] = useState('idle')
  const [slowLoad, setSlowLoad] = useState(false)
  const [refineSignal, setRefineSignal] = useState(0)
  const slowTimer = useRef(null)
  const pageViewFired = useRef(false)

  // --- natural-language search box ---------------------------------------
  const [nlText, setNlText] = useState('')
  const [nlBusy, setNlBusy] = useState(false)
  // True ONLY while the smart (model) call is actually in flight. The AI
  // affordance is tied to this and nothing else, so the motion always marks
  // real work: a smart pass that is disabled, rate limited or failing simply
  // clears this and claims nothing (web only — see components/AiAssist.jsx).
  const [nlSmartBusy, setNlSmartBusy] = useState(false)
  const [nlUnreadable, setNlUnreadable] = useState(false)
  const [nlUnresolved, setNlUnresolved] = useState([])
  const [nlRefined, setNlRefined] = useState(false)
  const [nlAmbiguous, setNlAmbiguous] = useState([])
  const [nlCandidates, setNlCandidates] = useState([])
  const [focus, setFocus] = useState([])
  // True only while a PARSE-produced single traveller is holding 2+ passports.
  // Fast mode can never split two people, so this is the manual escape hatch
  // for the ~3s before smart lands (and for when smart also gets it wrong).
  // Never set by hand-editing the form.
  const [splitAsk, setSplitAsk] = useState(false)
  // Field names the user has edited since the last parse. The smart pass skips
  // every one of them.
  const dirtyFields = useRef(new Set())
  // Bumped per parse so a slow smart response from an earlier sentence is dropped.
  const parseSeq = useRef(0)
  // The airport the user chose from the multi-airport question, remembered
  // beyond the member list it was written onto: the smart pass rebuilds that
  // list seconds later, and the answer must survive it.
  const pickedIata = useRef(null)
  // Set once the user presses the real search button: after that, a late smart
  // response must not disturb the results.
  const searched = useRef(false)

  // Fires once per page load, even under StrictMode's double-invoke.
  useEffect(() => {
    if (pageViewFired.current) return
    pageViewFired.current = true
    track('page_view')
  }, [])

  // Persist on every form edit so an in-app-browser reload can resume it.
  const updateTrip = (t) => {
    setTrip(t)
    saveFormState(t)
  }

  // Every edit the user makes by hand marks that field dirty, so a background
  // refinement can never take it back. Parse results go through updateTrip
  // directly and therefore never mark anything dirty.
  const onFormChange = (next) => {
    for (const key of PARSEABLE_FIELDS) {
      if (!sameValue(trip[key], next[key])) dirtyFields.current.add(key)
    }
    updateTrip(next)
  }

  // Write a parse result into the form. `respectDirty` is off for the instant
  // fast pass (the user has had no chance to type yet) and on for the smart
  // pass that lands seconds later.
  const applyParse = (res, { respectDirty }) => {
    const skip = (key) => respectDirty && dirtyFields.current.has(key)
    const candidates = res.home_iata_candidates || []

    let appliedMembers = null
    if (!skip('members')) {
      appliedMembers = normalizeMembers(res.payload?.members)
      // One sentence states one departure city for one trip, so it belongs to
      // EVERY traveller (the backend already puts a resolved home_iata on every
      // member — app/nlu/to_payload.py). Two cases have to be re-applied here:
      // exactly one airport for the named city, which the payload cannot carry
      // because the backend deliberately never picks one; and an airport the
      // user has already chosen from the multi-airport question, which would
      // otherwise be dropped by the member list this parse is replacing.
      // Never overwritten: a member who already has an airport keeps it.
      const iata = candidates.length === 1 ? candidates[0] : pickedIata.current
      if (iata) {
        appliedMembers = appliedMembers.map((m) =>
          m.home_iata ? m : { ...m, home_iata: iata }
        )
      }
    }

    setTrip((prev) => {
      const next = { ...prev }
      if (appliedMembers) next.members = appliedMembers
      if (!skip('budget_per_person_usd') && res.payload?.budget_per_person_usd != null) {
        next.budget_per_person_usd = res.payload.budget_per_person_usd
      }
      if (!skip('days') && res.payload?.days != null) next.days = res.payload.days
      if (!skip('rooms') && res.payload?.rooms != null) next.rooms = res.payload.rooms
      saveFormState(next)
      return next
    })
    // Ask only when this parse actually wrote the members, and left one person
    // holding several passports. A parse that already split them (or one whose
    // members were skipped as dirty) asks nothing.
    if (appliedMembers) {
      setSplitAsk(
        appliedMembers.length === 1 && (appliedMembers[0].passports?.length || 0) >= 2
      )
    }
    setNlUnresolved(res.unresolved || [])
    setNlAmbiguous(res.needs_disambiguation || [])
    setNlCandidates(candidates.length > 1 ? candidates : [])
    setFocus(res.focus || [])
  }

  // Both answers are the user's own statement about the group, so both mark
  // members dirty: a smart result landing later must not silently re-decide it.
  const keepOnePerson = () => {
    dirtyFields.current.add('members')
    setSplitAsk(false)
  }

  const splitTravellers = () => {
    dirtyFields.current.add('members')
    setSplitAsk(false)
    const first = trip.members[0]
    if (!first || (first.passports?.length || 0) < 2) return
    updateTrip({
      ...trip,
      members: first.passports.map((code, i) => ({
        name: i === 0 ? first.name || 'You' : `Traveller ${i + 1}`,
        passports: [code],
        home_iata: first.home_iata || '',
        // Credentials stay with traveller 1: the sentence never said whose
        // they are, and giving one to the wrong person changes their access.
        held_credentials: i === 0 ? first.held_credentials : []
      }))
    })
  }

  // Stage 1 fills the form immediately; stage 2 refines it in the background.
  // The UI is never blocked on stage 2 (p95 ~5s) and never errors out of it:
  // a rate limit or an outage just leaves the manual form standing.
  const runParse = async () => {
    const text = nlText.trim()
    if (!text || nlBusy) return
    const seq = ++parseSeq.current
    dirtyFields.current = new Set()
    pickedIata.current = null // a new sentence, a new departure city
    searched.current = false
    setNlBusy(true)
    setNlUnreadable(false)
    setNlRefined(false)
    setNlUnresolved([])
    setNlAmbiguous([])
    setNlCandidates([])
    setFocus([])
    setSplitAsk(false)

    let fast = null
    try {
      fast = await postParse(text, 'fast')
    } catch {
      // 429, offline, 5xx — all the same to the user: nothing happens, the
      // form below still works.
    }
    if (seq !== parseSeq.current) return
    setNlBusy(false)

    const fastUsable = fast != null && isUsable(fast)
    if (fast != null) {
      if (fastUsable) applyParse(fast, { respectDirty: false })
      else setNlUnreadable(true)
      track('nl_parse', { kind: `fast:${fastUsable ? 'used' : 'unused'}` })
    }

    // Stage 2, deliberately not awaited.
    setNlSmartBusy(true)
    postParse(text, 'smart')
      .then((smart) => {
        if (seq !== parseSeq.current) return // a newer sentence replaced this one
        if (searched.current) return // results are on screen; do not disturb them
        const smartUsable = isUsable(smart)
        const changed = fast == null || parseSignature(smart) !== parseSignature(fast)
        if (!smartUsable || !changed) {
          track('nl_parse', { kind: 'smart:unused' })
          return
        }
        applyParse(smart, { respectDirty: true })
        setNlUnreadable(false)
        setNlRefined(true)
        track('nl_parse', { kind: 'smart:used' })
      })
      .catch(() => {})
      .finally(() => {
        // Every ending clears the indicator — landed, unusable, rate limited,
        // failed, or thrown away because the results are already on screen. The
        // seq guard means a newer sentence's call keeps its own animation
        // running instead of being switched off by this older one.
        if (seq === parseSeq.current) setNlSmartBusy(false)
      })
  }

  // "Korea" -> the user picked one. Where it lands is decided by what the parse
  // already found, never by guessing the word's role: a traveller with no
  // passport yet is the "I'm Korean" shape, so it becomes their passport (and
  // is visible as a removable chip). Otherwise it is a place they asked about,
  // which the form has no field for — it becomes results emphasis.
  const addPassportToMember = (index, code) =>
    updateTrip({
      ...trip,
      members: trip.members.map((m, i) =>
        i === index && !m.passports.includes(code) ? { ...m, passports: [...m.passports, code] } : m
      )
    })

  // Where a resolved ambiguity lands.
  //
  //   person = an integer  -> that member's passport. Not a guess any more:
  //                           the parser said whose nationality it was, and
  //                           to_payload already emitted the member (with no
  //                           passport) so the position exists.
  //   person = null        -> nobody owns it: a destination, or a span the
  //                           model never assigned a role. Results emphasis.
  //   person absent        -> pre-2d shape. Falls back to the old heuristic.
  const resolveAmbiguity = (matched, code, person) => {
    setNlAmbiguous((prev) => prev.filter((a) => a.matched !== matched))

    if (Number.isInteger(person) && person >= 1 && person <= trip.members.length) {
      addPassportToMember(person - 1, code)
      return
    }
    if (person === null) {
      setFocus((prev) => (prev.includes(code) ? prev : [...prev, code]))
      return
    }

    // Fallback (person undefined): infer from whether traveller 1 has a
    // passport yet. "I'm Korean" leaves them empty; "travelling to Congo with
    // a Belgian passport" does not.
    if (!(trip.members?.[0]?.passports?.length > 0)) {
      addPassportToMember(0, code)
      return
    }
    setFocus((prev) => (prev.includes(code) ? prev : [...prev, code]))
  }

  // The answer to "which airport are you leaving from?" — one question about
  // one trip, so it lands on every traveller, not just the first. Writing it to
  // member 0 alone left the rest of the group with no origin, and the engine
  // then priced their flights from the no-origin average.
  //
  // It is remembered rather than marked dirty: dirtying `members` would also
  // throw away the traveller SPLIT the smart pass is about to deliver, so
  // applyParse re-applies this airport to the new member list instead. A
  // per-traveller override typed into the form afterwards goes through
  // onFormChange, which does mark members dirty, and is therefore safe from it.
  const pickAirport = (iata) => {
    setNlCandidates([])
    pickedIata.current = iata
    updateTrip({
      ...trip,
      members: trip.members.map((m) => ({ ...m, home_iata: iata }))
    })
  }

  const jurisdictionName = (code) =>
    meta?.passports?.find((p) => p.code === code)?.name || code

  // Whose card an ambiguity belongs to. Falls back to the positional label
  // when the traveller's name field has been cleared.
  const memberName = (person) =>
    trip.members?.[person - 1]?.name?.trim() ||
    (person === 1 ? 'You' : `Traveller ${person}`)

  const airportLabel = (iata) => {
    const a = meta?.airports?.find((x) => x.iata === iata)
    return a ? a.city || a.name : ''
  }

  const submit = async (t, persist = true) => {
    // Refresh the saved draft to match the search just run (req 4). A shared-URL
    // load passes persist=false so it never overwrites the user's own draft.
    if (persist) saveFormState(t)
    // From here on a late smart response is stale: the user has committed to
    // what the form said, and the answer is being computed from it.
    searched.current = true
    setView('loading')
    setError(null)
    setSlowLoad(false)
    clearTimeout(slowTimer.current)
    slowTimer.current = setTimeout(() => setSlowLoad(true), 4000)
    try {
      // Budget, days and rooms are optional: absent fields are omitted so the
      // server applies its defaults (days 7, rooms 1 per 2 travellers) and
      // skips budget bands entirely when no budget was given.
      const payload = {
        members: t.members.map((m) => ({
          name: m.name,
          passports: m.passports,
          // omitted when unset — the engine then uses an averaged flight estimate
          ...(m.home_iata ? { home_iata: m.home_iata } : {}),
          held_credentials: Array.isArray(m.held_credentials) ? m.held_credentials : []
        }))
      }
      if (Number(t.budget_per_person_usd) > 0) {
        payload.budget_per_person_usd = Number(t.budget_per_person_usd)
      }
      if (Number(t.days) >= 1) payload.days = Number(t.days)
      if (Number(t.rooms) >= 1) payload.rooms = Number(t.rooms)
      const body = await postFeasibility(payload)
      setResult(body)
      setView('results')
      // Fires once per completed query (submit runs once per query, not per re-render).
      track('query_completed')
      const url = `${window.location.pathname}?t=${encodeTrip(payload)}`
      window.history.replaceState(null, '', url)
    } catch (e) {
      setError(e.message)
      setView('form')
    } finally {
      clearTimeout(slowTimer.current)
    }
  }

  useEffect(() => {
    getMeta()
      .then(setMeta)
      .catch(() => setError('Could not reach the server — try reloading.'))
    // Precedence: a shared/result URL always wins over a saved draft. Only when
    // there is no (valid) shared link do we restore the in-progress form.
    let shared = null
    try {
      shared = tripFromUrl()
    } catch {
      setError("Couldn't read that shared trip link — starting fresh.")
      // A `t=` param that doesn't decode to a valid trip — a stale/corrupted
      // share, or (per the 7/27 traffic spike) a scanner probing t= as a
      // parameter name with garbage/absent payloads. Distinct from
      // query_completed never firing for an otherwise-valid shared trip.
      track('shared_link_invalid')
    }
    if (shared) {
      setTrip(shared)
      submit(shared, false) // don't overwrite the user's draft with a shared trip
    } else {
      const draft = tripFromDraft()
      if (draft) setTrip(draft)
    }
    return () => clearTimeout(slowTimer.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const share = async () => {
    const url = window.location.href
    try {
      if (navigator.share) {
        await navigator.share({ title: 'canwego — where can we all go?', url })
        return
      }
      await navigator.clipboard.writeText(url)
      setShareState('copied')
      setTimeout(() => setShareState('idle'), 2000)
    } catch {
      window.prompt('Copy this link and send it to the group:', url)
    }
  }

  // FORM gets a wider container (~1000px) so the traveller-card grid (2-up at
  // desktop) has room; RESULTS stays at the narrower 672px — its boarding-pass
  // cards' compact two-column data rows (flight/lodging/total) go sparse well
  // before 1000px, and each card's expandable <details> breakdown has variable
  // height, which would make a wide multi-column results grid ragged. LOADING
  // groups with results (not form) so there's no second width jump while the
  // (possibly several-second) query is in flight — the only shift happens once,
  // right at submit. Below sm:/lg:/md: both widths collapse to the same
  // mobile-full-width rendering (max-w-md's old value sits between them, so
  // mobile is unaffected either way; see the grid's md: breakpoint in
  // TripForm.jsx).
  const containerWidth = view === 'form' ? 'max-w-5xl' : 'max-w-2xl'

  return (
    <>
      <SiteNav />
      <main className="min-h-screen bg-cloud px-4 py-6 font-sans sm:px-6 lg:px-10">
        <div className={`mx-auto ${containerWidth}`}>
          {/* The shared SiteNav already carries the logo + "canwego" wordmark, so
              the old navy hero box here was duplicative. Left with just the
              tagline, promoted to the display face (Fraunces, navy) as a
              page-level intro line — 24px: clearly bigger than body text,
              clearly smaller than a homepage H2 (32px). */}
          <p className="mb-6 mt-6 font-display text-2xl text-ocean">
            Different passports and visas, one trip. See where your whole group
            can actually go.
          </p>

          {error && (
            <p className="mb-4 rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</p>
          )}

          {view === 'form' && (
            <>
              <SearchBox
                value={nlText}
                onChange={setNlText}
                onSubmit={runParse}
                busy={nlBusy}
                smartBusy={nlSmartBusy}
                unreadable={nlUnreadable}
                unresolved={nlUnresolved}
                onDismissUnresolved={() => setNlUnresolved([])}
                refined={nlRefined}
                onDismissRefined={() => setNlRefined(false)}
                ambiguous={nlAmbiguous}
                onResolve={resolveAmbiguity}
                nameFor={jurisdictionName}
                memberNameFor={memberName}
                candidates={nlCandidates}
                onPickAirport={pickAirport}
                airportLabel={airportLabel}
                focusNames={focus.map(jurisdictionName)}
              />
              <TripForm
                meta={meta}
                trip={trip}
                onChange={onFormChange}
                onSubmit={() => submit(trip)}
                // Nothing is submitted while a "which one did you mean?" is
                // still open. With the box untouched this is always false.
                busy={nlAmbiguous.length > 0}
                refineSignal={refineSignal}
                splitAsk={splitAsk}
                onKeepOnePerson={keepOnePerson}
                onSplitTravellers={splitTravellers}
              />
            </>
          )}

          {view === 'loading' && (
            <div className="flex flex-col items-center gap-4 py-16 text-center">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-ocean" />
              <p className="text-sm text-slate-600">Checking 200 destinations…</p>
              {slowLoad && (
                <p className="max-w-xs text-xs text-slate-400">
                  The free server naps when idle — waking it up can take up to a minute. Hang tight.
                </p>
              )}
            </div>
          )}

          {view === 'results' && result && (
            <Results
              result={result}
              focus={focus}
              onEdit={() => setView('form')}
              onShare={share}
              shareState={shareState}
              onRefineOrigin={() => {
                // one gesture: back to the form, refine section open, airport focused
                setView('form')
                setRefineSignal((n) => n + 1)
              }}
            />
          )}
        </div>
      </main>
    </>
  )
}
